<template>
    <div class="flex flex-col h-full w-full gap-2 tree-list-border">
        <div class="flex flex-col h-0 grow w-full">
            <dx-tree-list
                ref="treeListRef"
                v-model:expanded-row-keys="expandedRowKeys"
                :allow-column-reordering="true"
                :allow-column-resizing="meta.allowColumnResizing != null ? meta.allowColumnResizing : true"
                :cache-enabled="true"
                :column-auto-width="meta.columnAutoWidth || true"
                :column-min-width="70"
                :columnChooser="{ enabled: true, mode: 'select' }"
                :data-source="displayList"
                :filter-panel="{
                    visible: meta.findTool === 'filter' || false,
                    applyFilter: 'onClick',
                    filterEnabled: false,
                }"
                :filter-row="{ visible: meta.findTool === 'filter' || false, applyFilter: 'onClick' }"
                :filter-value="filterValueText"
                :fixing="{ enabled: true }"
                :focused-row-enabled="true"
                :hover-state-enabled="true"
                :load-panel="{ enabled: true }"
                :paging="{ enabled: meta.isPagingEnabled || false }"
                :parent-id-expr="meta.parentIdExpr"
                :repaint-changes-only="true"
                :root-value="meta.rootValue || null"
                :scrolling="{ mode: meta.scrollingMode || 'virtual' }"
                :selected-row-keys="preselectedRows"
                :selection="{
                    mode: meta.selectionMode || 'multiple',
                    allowSelectAll: meta.allowSelectAll || false,
                    recursive: true,
                }"
                :show-borders="true"
                :show-column-lines="true"
                :show-row-lines="true"
                :state-storing="{ enabled: true, type: 'custom', customSave: saveState, savingTimeout: 500 }"
                :word-wrap-enabled="meta?.wordWrapEnabled ?? true"
                class="h-0 grow"
                column-resizing-mode="widget"
                height="100%"
                key-expr="greyEminence"
                width="100%"
                @saved="saved"
                @option-changed="onOptionChanged"
                @selection-changed="selectionChanged"
                @focused-row-changing="onFocusedRowChanging"
                @focused-row-changed="onFocusedRowChanged"
                @row-click="onRowClick"
                @cell-click="onCellClick"
                @content-ready="contentReady"
                @context-menu-preparing="onContextMenuPreparing"
                @editor-prepared="onEditorPrepared"
                @editor-preparing="onEditorPreparing"
                @editing-start="onEditingStart"
            >
                <dx-toolbar :height="'64px'" :visible="toolbarShow">
                    <dx-item
                        v-for="(item, index) in toolbarConfigAfter"
                        :key="index"
                        :name="item.component.name"
                        location="after"
                    >
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            :disabled="refs[item.component.disabledRefName]"
                            :hint="
                                typeof item.component.hint === 'function' ? item.component.hint() : item.component.hint
                            "
                            class="ai-button-base"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                        <dx-text-box
                            v-if="item.widget === 'dx-text-box'"
                            v-model:value="refs[item.component.valueRefName.name]"
                            :label="
                                refs[item.component.valueLabelName]
                                    ? refs[item.component.valueLabelName]
                                    : item.component.valueLabelName
                            "
                            :width="item.component.width"
                            class="ai-toolbar-texteditor"
                            @enter-key="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'enter-key';
                                    eventController(event);
                                }
                            "
                            @key-up="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'key-up';
                                    eventController(event);
                                }
                            "
                        >
                            <DxTextBoxButton
                                v-for="(inputButton, indexButton) in item.component.inputButtons"
                                :key="indexButton"
                                :name="inputButton.name"
                                :options="inputButton.options"
                                location="after"
                            />
                        </dx-text-box>
                        <DxDropDownButton
                            v-if="item.widget === 'dx-drop-down-button'"
                            :dropDownOptions="item.component.dropDownOptions"
                            :hint="item.hint"
                            :icon="item.component.htmlElement"
                            :items="item.component.items"
                            :split-button="true"
                            @initialized="onDxDropDownButtonInitialized"
                        >
                            <dx-item v-for="(item, index) in item.component.items" :key="index">
                                <div
                                    :class="{
                                        'opacity-25': refs[item.component.disabledRefName],
                                        'ai-dx-drop-down-button-item dx-template-wrapper dx-item-content dx-list-item-content': true,
                                    }"
                                    @click="
                                        event => {
                                            event['item'] = item;
                                            event['eventName'] = 'click';
                                            eventController(event);
                                        }
                                    "
                                >
                                    <component
                                        :is="item.component.htmlElement"
                                        :disabled="refs[item.component.disabledRefName]"
                                        :isChecked="vIsCurrentSettingsUser"
                                        :name="item.component.name"
                                        class="ai-dx-drop-down-button-item-svg"
                                    />
                                    <div>
                                        {{
                                            typeof item.component.text === 'function'
                                                ? item.component.text()
                                                : item.component.text
                                        }}
                                    </div>
                                </div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>
                    <dx-item :visible="!!meta.title" location="before" name="titleGrid">
                        <div class="flex gap-2">
                            <h3>{{ `${meta.title}` }}</h3>
                        </div>
                    </dx-item>
                </dx-toolbar>
                <dx-column
                    v-if="meta.firstColumnFields"
                    :allow-editing="meta.firstColumnFields.allowEditing"
                    :allowReordering="false"
                    :allowSorting="true"
                    :caption="meta.firstColumnFields.caption"
                    :cell-template="'firstColumnCellTemplate'"
                    :column-min-width="400"
                    :data-field="meta.firstColumnFields.text"
                    :minWidth="300"
                    :sort-order="meta.defaultSort?.order"
                    headerCellTemplate="headerTemplate"
                />
                <dx-column
                    v-for="(item, index) in props.meta.columns"
                    :key="index"
                    :alignment="item.alignment || ''"
                    :allow-editing="item.allowEditing"
                    :allow-reordering="meta.firstColumnFields || index > 1"
                    :allowHiding="true"
                    :calculate-cell-value="calculateCellValue"
                    :calculate-display-value="item.calculateDisplayValue"
                    :caption="item.caption"
                    :cell-template="item.cellTemplate"
                    :component-meta="item.component"
                    :data-field="item.dataField"
                    :data-type="item.dataType || 'string'"
                    :edit-cell-template="chooseEditor(item)"
                    :editor-options="{
                        ...item.editorOptions,
                        maxLength: item.maxLength,
                        showClearButton: isDate(item),
                    }"
                    :editor-meta="item.editor"
                    :fixed="item.fixed"
                    :format="item.format"
                    :lookup="item.lookup || null"
                    :min-width="item.minWidth || 300"
                    :sort-index="item.sortIndex"
                    :sort-order="item.sortOrder"
                    :validation-rules="item.validationRules"
                    :visible="item.visible"
                    :width="item.width"
                    headerCellTemplate="headerTemplate"
                >
                    <DxLookup
                        v-if="item.editor?.type === 'ai-drop-down' || item.editor?.type === 'ai-popup'"
                        v-model:data-source="lookupList[item.dataField]"
                        :display-expr="item.editor?.text"
                        :value-expr="item.dataField"
                    />
                </dx-column>
                <dx-editing
                    :allow-adding="false"
                    :allow-deleting="false"
                    :allow-updating="editing?.allowUpdating"
                    mode="batch"
                    start-edit-action="click"
                />
                <dx-row-dragging
                    v-if="props.meta.dragging"
                    :allow-drop-inside-item="editing?.allowUpdating"
                    :allow-reordering="editing?.allowUpdating"
                    :on-drag-change="onDragChange"
                    :on-reorder="onDragReorder"
                    :onDragStart="onDragStart"
                    :show-drag-icons="editing?.allowUpdating"
                />
                <template #preferences>
                    <dx-check-box
                        :value="checkboxValue"
                        text="Автоматически сохранять настройки"
                        @value-changed="preferencesCheckChanged"
                    />
                </template>
                <template #cellImg="{ data }">
                    <div class="flex">
                        <component :is="data.text" />
                        <span class="pl-[10px]">{{ data.text }}</span>
                    </div>
                </template>
                <template #firstColumnCellTemplate="{ data }">
                    <div
                        :class="
                            'flex min-h-[16px] overflow-hidden ' +
                            highLightMatches(data.key, data.column.dataField, 'highlight-cell')
                        "
                    >
                        <component :is="data.data[props.meta.firstColumnFields.svg]" />
                        <span class="pl-[10px]">{{ data.data[props.meta.firstColumnFields.text] }}</span>
                    </div>
                </template>
                <template v-for="(_, name) in $slots" #[name]="{ data }">
                    <slot :data="data" :name="name" locate-in-menu="auto"></slot>
                </template>
                <template #dropDownBoxEditor="{ data: cellInfo }">
                    <ai-drop-down
                        :cellInfo="cellInfo"
                        :data-source="lookupList[cellInfo.column.dataField]"
                        :display-expr="cellInfo.column.editorMeta?.text"
                        :on-value-changed="cellInfo.setValue"
                        :value="cellInfo.value"
                        :value-expr="cellInfo.column.dataField"
                        @select="onLookUpSelect"
                        @end-editing="onEditingEnd"
                    />
                </template>
                <template #popupEditor="{ data: cellInfo }">
                    <ai-popup
                        :cell-info="cellInfo"
                        :display-value="cellInfo.displayValue"
                        :on-value-changed="cellInfo.setValue"
                        @select="onLookUpSelect"
                        @end-editing="onEditingEnd"
                        @on-field-clearing="onFieldClearing"
                    />
                </template>
                <template #headerTemplate="{ data: headerInfo }">
                    <div class="h-full flex items-start text-left line-clamp-3">
                        {{ headerInfo.column.caption }}
                    </div>
                </template>
            </dx-tree-list>
        </div>
        <grid-footer v-if="showGridFooter" ref="childRef" />
    </div>
</template>
<script setup>
/* Чтобы ограничить количество символов, которые можно добавить в поле ввода, необходимо в мету колонки прописать настройку 'maxLength: 250' (например, 250 символов)*/
import { DxColumn, DxEditing, DxItem, DxLookup, DxRowDragging, DxToolbar, DxTreeList } from 'devextreme-vue/tree-list';
import { DxButton as DxTextBoxButton, DxTextBox } from 'devextreme-vue/text-box';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { DxButton } from 'devextreme-vue';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { computed, onBeforeMount, onMounted, onUnmounted, reactive, ref } from 'vue';
import ContextMenu from 'devextreme/ui/context_menu';
import gridFooter from '../grid-footer.vue';
import deepEqual from '@/utils/deepEqual.js';
import AiDropDown from '@/components/ai-drop-down.vue';
import { aiTreeNodeLoadStoreLocalSetProp } from '@/components/ai-tree-node-load/ai-tree-node-load-store';

/*
Чтобы слова в заголовках столбцов НЕ переносились необходимо добавить в мету этой таблицы настройку 'wordWrapEnabled: false,'
*/

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    dataSource: {
        type: Array,
        default: () => [],
    },
    lockedRows: {
        type: Array,
        default: () => [],
    },
    editing: {
        type: Object,
        default: () => {},
    },
    toolbarShow: {
        type: Boolean,
        default: () => true,
    },
    toolbarConfig: {
        type: Object,
        default: () => {},
    },
    preferencesState: {
        type: Boolean,
    },
    lookupsData: {
        type: Object,
        default: () => {},
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['moveFrom', 'moveTo', 'choice', 'edit'],
    },
    showGridFooter: {
        type: Boolean,
        default: () => true,
    },
    args: {
        type: Object,
        default: () => {},
    },
    preselectedRows: {
        type: Array,
        default: () => [],
    },
    changeRouteComponent: {
        type: Boolean,
        default: () => false,
    },
});
const findValue = ref();
const findResult = ref('Введите текст');
const findPosition = ref(0);
const refs = ref({
    isChoiceButtonDisabled: true,
    isSaveButtonDisabled: true,
    isDeleteButtonDisabled: true,
    isCopyButtonDisabled: true,
    isCutButtonActive: true,
    isPutButtonDisable: true,
    isEditButtonDisabled: true,
    expandOrCollapseButtonStateHint: 'Развернуть все',
    isMoveSaveButtonDisabled: true,
    isMoveToButtonDisabled: true,
    isSaveSettingsButtonDisabled: true,
    isAddSiblingButtonDisabled: false,
    isAddChildButtonDisabled: false,
    findValue: findValue,
    findResult: findResult,
});

const filterValueText = ref([]);
const searchQuery = ref('');
const userData = ref({
    selectedRowKeys: [],
    treeListChanges: [],
    reorderedRows: [],
    searchPanelValue: searchQuery.value,
});
const treeListRef = ref();
const treeListInstance = ref();
const vIsCurrentSettingsUser = ref(true);

let toolbarConfigAfter = [];

const toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'choice',
            disabledRefName: 'isChoiceButtonDisabled',
            hint: 'Выбрать и закрыть',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-choice-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-base',
        },
    },
    // region find group start
    {
        widget: 'dx-button',
        component: {
            name: 'find-prev',
            hint: 'Предыдущий',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-find-prev-base',
        },
    },
    {
        widget: 'dx-text-box',
        component: {
            name: 'find',
            emits: [{ name: 'enter-key' }, { name: 'key-up' }],
            htmlElement: 'ai-find-prev-base',
            valueLabelName: 'findResult',
            valueRefName: { name: 'findValue' },
            width: 250,
            inputButtons: [
                {
                    name: 'filter-clear',
                    options: {
                        icon: `    <svg class='ai-clear-button-color' width='22' height='22' viewBox='-4 -5 22 22' xmlns='http://www.w3.org/2000/svg'>
                        <rect x='11.2729' width='1.5' height='15' rx='0.5' transform='rotate(45 11.2729 0)'/>
                        <rect x='0.666504' y='1.06055' width='1.5' height='15' rx='0.5' transform='rotate(-45 0.666504 1.06055)'/>
                       </svg>`,
                        stylingMode: 'text',
                        hint: 'Очистить',
                        onClick: () => {
                            findValue.value = '';
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findValue', findValue.value);
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findPosition', undefined);
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findResultData', undefined);
                            findResult.value = 'Введите текст';
                        },
                    },
                },
            ],
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'find-start',
            hint: 'Начать поиск',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-start-find-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'find-next',
            hint: 'Следующий',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-find-next-base',
        },
    },
    // endregion find group end
    {
        widget: 'dx-button',
        component: {
            name: 'expandOrCollapseButton',
            hint: () => {
                return toolbarState.expandOrCollapseButtonState.hint;
            },
            emits: [{ name: 'click' }],
            htmlElement: 'ai-expand-or-collapse-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'copy',
            disabledRefName: 'isCopyButtonDisabled',
            hint: 'Создать копию сфокусированной строки',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-copy-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'cut',
            disabledRefName: 'isCutButtonActive',
            hint: 'Перемещение, укажите куда',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-cut-svg',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'put',
            disabledRefName: 'isPutButtonDisable',
            hint: 'Перемещение, переместить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-put-svg',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'edit',
            disabledRefName: 'isEditButtonDisabled',
            hint: 'Редактировать',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-edit-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'addSibling',
            hint: 'Добавить на текущем уровне',
            disabledRefName: 'isAddSiblingButtonDisabled',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-sibling-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'addChild',
            hint: 'Добавить на подчиненном уровне',
            disabledRefName: 'isAddChildButtonDisabled',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-child-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'delete',
            disabledRefName: 'isDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'save',
            disabledRefName: 'isSaveButtonDisabled',
            hint: 'Сохранить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-save-base',
        },
    },
    {
        widget: 'searchPanel',
        component: {
            name: 'searchPanel',
        },
    },
    {
        widget: 'dx-drop-down-button',
        hint: 'Экспортировать',
        component: {
            name: 'export',
            htmlElement: `<svg width='32' height='32' viewBox='0 0 76 74' fill='none' xmlns='http://www.w3.org/2000/svg'>
<g clip-path='url(#clip0_1695_4138)'>
<path fill-rule='evenodd' clip-rule='evenodd' d='M5 0C2.23858 0 0 2.23858 0 5V69C0 71.7614 2.23858 74 5 74H57C59.7614 74 62 71.7614 62 69V21H44C42.3431 21 41 19.6569 41 18V0H5Z' fill='#286EB6'/>
<path d='M45 1L53 9L61 17H48C46.3431 17 45 15.6569 45 14V1Z' fill='#286EB6'/>
<path d='M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879ZM38.2068 51.2919L52.8038 51.2879ZM52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z' fill='white'/>
<path d='M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879M52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z' stroke='#286EB6' stroke-width='4'/>
<path d='M32 41C32 39.8954 32.8954 39 34 39H55V49H34C32.8954 49 32 48.1046 32 47V41Z' fill='white'/>
</g>
<defs>
<clipPath id='clip0_1695_4138'>
<rect width='76' height='74' fill='white'/>
</clipPath>
</defs>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-all-base',
                        name: 'export-pdf-all',
                        text: 'Экспортировать все в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-selected-base',
                        name: 'export-pdf-selected',
                        text: 'Экспортировать выбранные строки в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-all-base',
                        name: 'export-excel-all',
                        text: 'Экспортировать всё в EXCEL',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-selected-base',
                        name: 'export-excel-selected',
                        text: 'Экспортировать выбранные строки в EXCEL',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-drop-down-button',
        hint: 'Настройки',
        component: {
            name: 'settings',
            htmlElement: `<svg width='79' height='79' viewBox='0 0 79 79' fill='none' xmlns='http://www.w3.org/2000/svg'>
<path fill-rule='evenodd' clip-rule='evenodd' d='M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z' fill='#286EB6'/>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-auto-settings-save',
                        text: 'Автоматически сохранять настройки',
                        isChecked: vIsCurrentSettingsUser.value,
                        htmlElement: 'ai-checkbox',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-user-settings',
                        text: 'Сохранить настройки',
                        disabledRefName: 'isSaveSettingsButtonDisabled',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'clear-user-settings',
                        text: 'Сбросить настройки',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'show-column-chooser',
            hint: 'Выбор столбцов',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-show-column-chooser-base',
        },
    },
];
const timeoutId = ref(null);
const lookupAddData = ref({});
const checkboxValue = computed(() => props.preferencesState);
const userSettingsData = ref({});
const reorderedRows = ref([]);
const expandedRowKeys = ref([]);
const childRef = ref(null);
const rowToCut = ref();
const filterDelayTimer = ref(null);
const filterMatches = ref(new Set());
const searchPanelInstance = ref();
const filterRowInstances = ref({});
const columnFilters = ref({});
const toolbarState = reactive({
    expandOrCollapseButtonState: {
        hint: 'Развернуть все',
        icon: 'expand',
        instance: null,
    },
    selectButtonState: {
        instance: null,
        disabled: true,
    },
    deleteButtonState: {
        instance: null,
        disabled: true,
    },
    copyButtonState: {
        instance: null,
        disabled: true,
    },
    preferencesButtonState: {
        instance: null,
        items: null,
    },
    saveChangesButtonState: {
        instance: null,
        disabled: true,
    },
    moveSelectButtonState: {
        instance: null,
        disabled: true,
    },
});

const metaColumns = computed(() => props.meta.columns.map(col => col.dataField));
const booleanColumns = computed(() =>
    props.meta.columns.filter(col => col.dataType === 'boolean').map(col => col.dataField)
);

onBeforeMount(() => {
    // toolbar

    for (const item of toolbarConfigInitAfter) {
        if (!props.toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }

        if (props.meta.findTool && props.meta.findTool !== 'search') {
            toolbarConfigAfter = toolbarConfigAfter.filter(
                item => !['find-next', 'find-start', 'find', 'find-prev'].includes(item.component.name)
            );
        }
    }

    if (props.meta.toolbarItems && props.meta.toolbarItems.length) {
        for (const item of props.meta.toolbarItems) {
            toolbarConfigInitAfter.push(item); // Добавляем элементы из meta.toolbarItems
        }
    }

    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };

    const toolbarUserItems = [...props.toolbarUserItems].sort(compare);

    for (const item of toolbarUserItems) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }

    return props.meta.columns.map(col => col);
});
onMounted(async () => {
    document.addEventListener('keydown', handleKeyDown);
    treeListInstance.value = treeListRef.value.instance;
    emit('treeListEditCoreMounted');
    setSelected(props.preselectedRows);
});

const handleKeyDown = event => {
    if (event.key === 'Escape') {
        if (cutRow.value) {
            // Снимаем подсветку
            resetRowColor(cutRow.value.key);
            //очищаем строку
            cutRow.value = null;
        }
        //Дисейблим кнопку в тулбаре
        refs.value['isPutButtonDisable'] = !cutRow.value;
    }
};

const isDate = (column) => column.dataType === 'date' || column.dataType === 'datetime';

const onFieldClearing = cell => {
    cell.text = '';
    treeListInstance.value.refresh();
};

const eventController = async e => {
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
    if (e.item.widget === 'dx-text-box') {
        if (e.eventName === 'enter-key' || e.eventName === 'key-up') {
            await toolbarTextboxEvent(e);
        }
    }
    if (e.item.widget === 'dx-drop-down-item') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
};

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'refresh':
            await onRefreshClick(e);
            break;
        case 'copy':
            await onCopyClick(e);
            break;
        case 'addSibling':
            await onAddRootRowClick(e);
            break;
        case 'addChild':
            await onAddChildRowClick(e);
            break;
        case 'delete':
            await onDeleteClick(e);
            break;
        case 'save':
            await onSaveClick(e);
            break;
        case 'expandOrCollapseButton':
            onExpandOrCollapseClick(e);
            break;
        case 'cut':
            await onCutClick(e);
            break;
        case 'put':
            await onPutClick(e);
            break;
        case 'apply-auto-settings-save':
            applyAutoSettingsSave(e);
            break;
        case 'apply-user-settings':
            applyUserSettings(e);
            break;
        case 'clear-user-settings':
            clearUserSettings(e);
            break;
        case 'show-column-chooser':
            treeListRef.value?.instance.showColumnChooser();
            break;
        case 'find-start':
            await findStart(e);
            break;
        case 'find-next':
            await findNext(e);
            break;
        case 'find-prev':
            await findPrev(e);
            break;
        default:
            break;
    }
    emit('toolbarButtonClick', e);
};
const findResultData = ref([]);

async function findStart(e) {
    if (findValue.value?.length > 0) {
        console.log('e:', e, 'findValue:', findValue.value);
        props.meta.columns;
        props.dataSource;

        // Получаем все колонки.
        const columns = treeListRef.value.instance.option('columns');

        // Фильтруем только видимые колонки
        const visibleColumns = columns.filter(column => column.visible !== false);
        const findsArray = [];
        const visibleColumnsDataFields = visibleColumns.map(column => column.dataField);
        for (const row of props.dataSource) {
            let findFlag = false;
            for (const field of visibleColumnsDataFields) {
                if (row[field]) {
                    if (row[field].toString().includes(findValue.value)) {
                        findFlag = true;
                        break;
                    }
                }
            }
            if (findFlag) {
                findsArray.push(row);
            }
        }
        // greyEminence
        const keyExpr = props.meta.keyExpr;

        // Массив для хранения ключей узлов в порядке их следования
        const nodeOrder = [];

        // Обходим все узлы с помощью forEachNode
        treeListRef.value.instance.forEachNode(node => {
            nodeOrder.push(node.key); // Сохраняем ключи узлов в порядке их следования
        });

        // Сортируем findDataSource по порядку ключей
        const sortedFindsArray = nodeOrder
            .map(key => findsArray.find(item => item[keyExpr] === key)) // Сопоставляем ключи с данными
            .filter(item => item !== undefined); // Убираем undefined (если какие-то ключи не найдены)
        if (sortedFindsArray.length > 0) {
            findPosition.value = 0;
            // focusedRowKey.value = null;
            await setFocus(sortedFindsArray[0][keyExpr]);
            findResult.value = `Найдено: ${sortedFindsArray.length} позиция [1]`;
        } else {
            findResult.value = 'Найдено: 0';
        }
        findResultData.value = sortedFindsArray;
    } else {
        findResult.value = `Введите текст`;
        findResultData.value = [];
    }
}

const findNext = async () => {
    if (findPosition.value < findResultData.value.length - 1) {
        findPosition.value = findPosition.value + 1;
        const row = findResultData.value[findPosition.value];
        const keyExpr = props.meta.keyExpr;
        await setFocus(row[keyExpr]);
        findResult.value = `Найдено: ${findResultData.value.length} позиция [${findPosition.value + 1}]`;
    }
};
const findPrev = async () => {
    if (findPosition.value > 0) {
        findPosition.value = findPosition.value - 1;
        const row = findResultData.value[findPosition.value];
        const keyExpr = props.meta.keyExpr;
        await setFocus(row[keyExpr]);
        findResult.value = `Найдено: ${findResultData.value.length} позиция [${findPosition.value + 1}]`;
    }
};
const toolbarTextboxEvent = async e => {
    switch (e.eventName) {
        case 'enter-key':
            switch (e.item.component.name) {
                case 'find':
                    await onEnterKeyFilter(e);
                    break;
                default:
                    break;
            }
            break;
        case 'key-up':
            switch (e.item.component.name) {
                case 'find':
                    await onFilterKeyup(e);
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
    emit('toolbarTextboxEvent', e);
};
const onEnterKeyFilter = e => {
    console.log('onEnterKeyFilter($event): ', e);
};
const onFilterKeyup = async e => {
    if (e.item.component.name === 'find') {
        findValue.value = e.event.currentTarget.value;
        console.log('onFilter1Keyup($event): ', e.event.currentTarget.value);
    }
};
//---------------------------Cut&Put
const isDescendant = (parentKey, childKey) => {
    // Получаем узел childKey (от которого будем идти вниз)
    const startNode = treeListInstance.value.getNodeByKey(childKey);
    if (!startNode) return false;
    // обход в глубину (DFS)
    const stack = [startNode];
    while (stack.length > 0) {
        const currentNode = stack.pop();
        // Проверяем всех детей текущего узла
        for (const childNode of currentNode.children) {
            if (childNode.key === parentKey) {
                return true; // Нашли parentKey среди потомков
            }
            stack.push(childNode);
        }
    }
    return false;
};

const cutRow = ref(null);

const highlightRow = (rowKey, color) => {
    const rowIndex = treeListInstance.value.getRowIndexByKey(rowKey);
    if (rowIndex) {
        const rowElements = treeListInstance.value.getRowElement(rowIndex);
        if (rowElements) {
            if (Array.isArray(rowElements)) {
                rowElements.forEach(element => {
                    element.style.backgroundColor = color;
                    console.log(element, color);
                });
            } else {
                rowElements.style.backgroundColor = color;
            }
        }
    }
};

const resetRowColor = rowKey => {
    highlightRow(rowKey, '');
};

const onCutClick = async () => {
    //Cнимаем подсветку с предыдущей строки
    if (cutRow.value) {
        resetRowColor(cutRow.value.key);
    }
    cutRow.value = focusedRow.value;
    //Расдисейбливаем кнопку в тулбаре
    refs.value['isPutButtonDisable'] = !cutRow.value;
    // Подсвечиваем вырезанную строку
    highlightRow(cutRow.value.key, '#FFDD80');
};

const onPutClick = async () => {
    if (!cutRow.value) {
        return;
    }
    const targetRow = focusedRow.value;
    //Заполняем userChanges
    updateChanges(
        cutRow.value.key,
        props.meta.parentIdExpr,
        targetRow.data[props.meta.keyExpr],
        cutRow.value[props.meta.parentIdExpr]
    );
    //Пересобираем строки для отображения изменений
    const match = reorderedRows.value.find(row => row.key === cutRow.value.key);
    if (match) {
        updateReorderedRows(cutRow.value.key, targetRow.data[props.meta.keyExpr]);
    } else {
        addNewReorderedRow(cutRow.value.key, targetRow.data[props.meta.keyExpr]);
    }
    // Снимаем подсветку
    resetRowColor(cutRow.value.key);
    //очищаем строку
    cutRow.value = null;
    //Дисейблим кнопку в тулбаре
    refs.value['isPutButtonDisable'] = true;
    emit('cutPutEnd');
};
//---------------------------
const clearUserSettings = () => {
    emit('userSettingsReset');
};

const applyAutoSettingsSave = () => {
    refs.value['isSaveSettingsButtonDisabled'] = !vIsCurrentSettingsUser.value;
    vIsCurrentSettingsUser.value = !vIsCurrentSettingsUser.value;
};

const applyUserSettings = () => {
    emit('save-settings');
};

const lookupList = computed(() => {
    const resObj = {};

    for (const dsKey of Object.keys(props.lookupsData)) {
        for (const dsRow of props.lookupsData[dsKey]) {
            if (resObj[dsKey] === undefined) {
                resObj[dsKey] = [];
            }

            const findRes = resObj[dsKey].find(item => {
                if (item[dsKey] === dsRow[dsKey]) {
                    return true;
                }
            });

            if (findRes === undefined) {
                resObj[dsKey].push(dsRow);
            }
        }
    }
    for (const dsKey of Object.keys(lookupAddData.value)) {
        for (const dsRow of lookupAddData.value[dsKey]) {
            if (resObj[dsKey] === undefined) {
                resObj[dsKey] = [];
            }
            const findRes = resObj[dsKey].find(item => {
                if (item[dsKey] === dsRow[dsKey]) {
                    return true;
                }
            });
            if (findRes === undefined) {
                resObj[dsKey].push(dsRow);
            }
        }
    }

    return { ...resObj };
});

const onLookUpSelect = event => {
    if (lookupAddData.value[event.keyExpr] === undefined) {
        lookupAddData.value[event.keyExpr] = [];
    }
    lookupAddData.value[event.keyExpr].push(event.row);
};

const displayList = computed(() => {
    const { keyExpr, parentIdExpr } = props.meta;

    const reorderedMap = reorderedRows.value.reduce((acc, obj) => {
        acc[obj.key] = obj;

        return acc;
    }, {});

    const processedData = [...props.dataSource].map(row => {
        if (!row.greyEminence) {
            row.greyEminence = row[keyExpr];
        }

        const match = reorderedMap[row.greyEminence];
        if (match) row[parentIdExpr] = match.updatedParentId;

        return row;
    });

    return filterTreeListData(processedData, searchQuery.value, columnFilters.value);
});

const onEditingEnd = ({ rowIndex, columnIndex }) => {
    editCell(rowIndex, columnIndex);
};

const editCell = (rowIndex, columnIndex) => {
    setTimeout(async () => {
        let isNearestRowExist = !!(await treeListInstance.value.getKeyByRowIndex(rowIndex + 1));
        let nearestRowIndex = rowIndex + 1;
        if (!isNearestRowExist) {
            if (rowIndex === 0) return;

            isNearestRowExist = !!(await treeListInstance.value.getKeyByRowIndex(rowIndex - 1));
            nearestRowIndex = rowIndex - 1;

            if (!isNearestRowExist) return;
        }
        await treeListInstance.value.editCell(nearestRowIndex, columnIndex);
        await treeListInstance.value.editCell(rowIndex, columnIndex);
    }, 0);
};

const chooseEditor = ({ editor, editCellTemplate }) => {
    if (!editor?.type) return editCellTemplate;

    if (editor?.type === 'ai-drop-down') return 'dropDownBoxEditor';

    if (editor.type === 'ai-popup') return 'popupEditor';
};

const emit = defineEmits([
    'treeListEditCoreMounted',
    'refreshClick',
    'selectClick',
    'saveClick',
    'deleteClick',
    'coreContextMenuPrepared',
    'userSettingsChanged',
    'userDataChanged',
    'userSettingsReset',
    'dragChange',
    'dragReorder',
    'cutPutEnd',
    'cellClick',
    'cellValueChanged',
    'toolbarButtonClick',
    'save-settings',
    'escapeDownClick',
    'treeListFocusChanged',
    'changesSavedSuccessfully',
]);

const saveState = state => {
    const { selectedRowKeys, searchText, filterValue, columns } = state;

    const newUserSettings = {
        searchText,
        filterValue,
        columns,
        preferencesState: props.preferencesState,
        filterRowValues: columnFilters.value,
    };

    const isSettingsHasChanged = !deepEqual(userSettingsData.value, newUserSettings);
    userSettingsData.value = newUserSettings;

    if (props.preferencesState && isSettingsHasChanged) {
        emit('userSettingsChanged', userSettingsData.value, vIsCurrentSettingsUser.value);
    }

    const newUserData = {
        ...userData.value,
        selectedRowKeys,
    };
    const isUserDataHasChanged = !deepEqual(userData.value, newUserData);
    userData.value = newUserData;

    if (isUserDataHasChanged && vIsCurrentSettingsUser.value) emit('userDataChanged', userData.value);
};

const filterTreeListData = (dataSource, searchQueryValue, columnFilters) => {
    const isGlobalSearchActive = searchQueryValue && searchQueryValue.length > 2;
    const isColumnFiltersActive = Object.values(columnFilters).some(value => value != null && value !== '');

    if (!isGlobalSearchActive && !isColumnFiltersActive) {
        filterMatches.value = new Map();
        setTimeout(() => {
            collapseAll();
        }, 1000);
        return dataSource;
    }

    const { parentIdExpr, keyExpr } = props.meta;
    const data = [...dataSource];

    const visibleColumns = treeListInstance.value.getVisibleColumns().map(col => col.dataField);

    const idToNode = new Map();
    const childrenMap = new Map();
    const searchMatches = new Map();

    data.forEach(node => {
        const nodeId = node[keyExpr];
        const parentId = node[parentIdExpr];

        idToNode.set(nodeId, node);

        if (!childrenMap.has(parentId)) {
            childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId).push(nodeId);

        let nodeMatchesAllColumnFilters = true;
        const matchedFields = new Set();

        visibleColumns.forEach(field => {
            if (!nodeMatchesAllColumnFilters) return;

            const columnFilterValue = columnFilters[field];

            if (columnFilterValue != null) {
                const fieldValue = node[field];

                if (fieldValue != null) {
                    const fieldValueStr = fieldValue.toString().toLowerCase();
                    const columnFilterValueStr = columnFilterValue.toString().toLowerCase();

                    if (fieldValueStr.includes(columnFilterValueStr)) {
                        matchedFields.add(field);
                    } else {
                        nodeMatchesAllColumnFilters = false;
                    }
                } else {
                    nodeMatchesAllColumnFilters = false;
                }
            }
        });

        let nodeMatchesGlobalSearch;

        if (isGlobalSearchActive) {
            const searchQueryStr = searchQueryValue.toString().toLowerCase();

            nodeMatchesGlobalSearch = visibleColumns.some(field => {
                const fieldValue = node[field];

                if (fieldValue != null) {
                    const fieldValueStr = fieldValue.toString().toLowerCase();

                    if (fieldValueStr.includes(searchQueryStr)) {
                        matchedFields.add(field);
                        return true;
                    }
                }
                return false;
            });
        } else {
            nodeMatchesGlobalSearch = true;
        }

        if (nodeMatchesAllColumnFilters && nodeMatchesGlobalSearch && matchedFields.size > 0) {
            searchMatches.set(nodeId, matchedFields);
        }
    });

    const results = new Set();
    const ancestorsToExpand = new Set();

    const addAncestors = nodeId => {
        const node = idToNode.get(nodeId);
        if (node) {
            const parentId = node[parentIdExpr];
            if (parentId != null && !results.has(parentId)) {
                results.add(parentId);
                ancestorsToExpand.add(parentId);
                addAncestors(parentId);
            }
        }
    };

    const addDescendants = nodeId => {
        if (childrenMap.has(nodeId)) {
            childrenMap.get(nodeId).forEach(childId => {
                if (!results.has(childId)) {
                    results.add(childId);
                    addDescendants(childId);
                }
            });
        }
    };

    searchMatches.forEach((fields, nodeId) => {
        results.add(nodeId);
        addAncestors(nodeId);
        addDescendants(nodeId);
    });

    filterMatches.value = searchMatches;

    expandAncestorsAfterFiltering(ancestorsToExpand);

    return data.filter(node => results.has(node[keyExpr]));
};

const expandAncestorsAfterFiltering = async ancestorsSet => {
    await delay(200);

    if (searchQuery.value || columnFilters.value) {
        ancestorsSet.forEach(key => {
            treeListInstance.value.expandRow(key);
        });
    } else {
        collapseAll();
    }
};

function calculateCellValue(rowData) {
    if (this.dataType !== 'boolean') return rowData[this.dataField];

    if (!props.meta.enableThreeStateBehavior && rowData[this.dataField] === null) {
        return 0;
    }

    return rowData[this.dataField];
}

const addParentToExpanded = (row, currentExpandedRowKeys) => {
    if (row[props.meta.parentIdExpr]) {
        if (!currentExpandedRowKeys.includes(row[props.meta.parentIdExpr])) {
            currentExpandedRowKeys.push(row[props.meta.parentIdExpr]);
        }
        const parentRow = displayList.value.find(item => item[props.meta.keyExpr] === row[props.meta.parentIdExpr]);
        if (parentRow) {
            addParentToExpanded(parentRow, currentExpandedRowKeys);
        }
    }
};

const selectionChanged = event => {
    const { selectedRowKeys, selectedRowsData } = event;
    const { selectButtonState, deleteButtonState } = toolbarState;

    childRef.value?.selectRows(selectedRowKeys.length);
    emit('selectClick', event);

    let currentExpandedRowKeys = [...expandedRowKeys.value];
    if (selectedRowKeys.length > 0) {
        refs.value['isDeleteButtonDisabled'] = false;
        deleteButtonState.disabled = false;
        deleteButtonState.instance?.option('disabled', false);
        if (props.meta.selectButtonMode === 'single') {
            if (selectedRowKeys.length === 1) {
                selectButtonState.disabled = false;
                selectButtonState.instance?.option('disabled', false);
            } else {
                selectButtonState.disabled = true;
                selectButtonState.instance?.option('disabled', true);
            }
        } else {
            selectButtonState.disabled = false;
            selectButtonState.instance?.option('disabled', false);
        }
    } else {
        refs.value['isDeleteButtonDisabled'] = true;
        deleteButtonState.disabled = true;
        deleteButtonState.instance?.option('disabled', true);
        selectButtonState.disabled = true;
        selectButtonState?.instance?.option('disabled', true);
    }

    if (props.meta.parentIdExpr && selectedRowKeys.length > 0) {
        selectedRowsData.forEach(row => {
            addParentToExpanded(row, currentExpandedRowKeys);
        });
    }

    setTimeout(() => {
        if (!deepEqual(expandedRowKeys.value, currentExpandedRowKeys)) {
            expandedRowKeys.value = currentExpandedRowKeys;
        }
    }, 0);
};

const contentReady = () => {
    childRef.value?.setTotalCount(displayList.value.length);

    const {
        expandOrCollapseButtonState,
        deleteButtonState,
        selectButtonState,
        copyButtonState,
        preferencesButtonState,
        saveChangesButtonState,
        moveSelectButtonState,
    } = toolbarState;
    if (!expandOrCollapseButtonState.instance) {
        expandOrCollapseButtonState.instance = { option: {} };
    }
    if (expandOrCollapseButtonState.instance) {
        expandOrCollapseButtonState.instance.option['icon'] = expandOrCollapseButtonState.icon;
        expandOrCollapseButtonState.instance.option['hint'] = expandOrCollapseButtonState.hint;
    }

    deleteButtonState.instance?.option('disabled', deleteButtonState.disabled);
    selectButtonState.instance?.option('disabled', selectButtonState.disabled);
    copyButtonState.instance?.option('disabled', copyButtonState.disabled);
    moveSelectButtonState.instance?.option('disabled', moveSelectButtonState.disabled);
    const saveButtonDisable = userData.value.treeListChanges.length <= 0;
    saveChangesButtonState.instance?.option('disabled', saveButtonDisable);
    saveChangesButtonState.disabled = saveButtonDisable;

    if (preferencesButtonState.items) preferencesButtonState.instance?.option('items', preferencesButtonState.items);

    searchPanelInstance.value?.option('value', searchQuery.value);
};

const onFocusedRowChanging = event => {
    const { rows, newRowIndex } = event;
    const rowKey = rows.find(el => el.dataIndex === newRowIndex)?.key;

    if (props.lockedRows.includes(rowKey)) event.cancel = true;
};

const focusedRow = ref();
const focusedRowIndex = ref();

const onFocusedRowChanged = async ({ rowIndex, row, component }) => {
    if (!row) {
        console.log('ai-tree-list-edit-core onFocusedRowChanged error: rowIndex === undefined');
        return;
    }

    refs.value['isCopyButtonDisabled'] = false;
    refs.value['isCutButtonActive'] = false;
    focusedRow.value = row;
    focusedRowIndex.value = row?.rowIndex;
    const focusedKey = treeListInstance.value.option('focusedRowKey');
    const { moveSelectButtonState, copyButtonState, selectButtonState, deleteButtonState } = toolbarState;

    if (focusedKey) {
        copyButtonState.disabled = false;
        copyButtonState.instance?.option('disabled', false);
    } else {
        copyButtonState.disabled = true;
        copyButtonState.instance?.option('disabled', true);
    }
    if (rowIndex !== 0 && rowIndex !== -1) {
        emit('treeListFocusChanged', row, props.meta.viewId, rowIndex, component);
    }

    if (props.meta.selectionMode === 'single') {
        if (focusedKey) {
            selectButtonState.disabled = false;
            selectButtonState.instance?.option('disabled', false);
            refs.value['isDeleteButtonDisabled'] = false;
            deleteButtonState.disabled = false;
            deleteButtonState.instance?.option('disabled', false);

            refs.value['isChoiceButtonDisabled'] = false;
            childRef.value?.selectRows(1);
        } else {
            selectButtonState.disabled = true;
            selectButtonState.instance?.option('disabled', true);
            refs.value['isChoiceButtonDisabled'] = true;
        }
    }
    refs.value['isMoveToButtonDisabled'] = false;
    moveSelectButtonState.disabled = false;
    moveSelectButtonState.instance?.option('disabled', false);
};

const onRowClick = ({ key, event }) => {
    if (props.lockedRows.includes(key)) event.stopImmediatePropagation();
};

const onCellClick = ({ key, event }) => {
    if (props.lockedRows.includes(key)) event.stopImmediatePropagation();

    emit('cellClick', props.meta.viewId, event, key);
};
let eventEditRowKey = null;
let eventEditColumnName = null;
const onOptionChanged = event => {
    if (event.fullName !== 'hoveredElement') {
        if (event.fullName === 'editing.editRowKey') {
            eventEditRowKey = event.value;
        }
        if (event.fullName === 'editing.editColumnName') {
            eventEditColumnName = event.value;
        }
        if (event.fullName === 'editing.changes') {
            userData.value = {
                ...userData.value,
                treeListChanges: [...event.value],
            };
            if (eventEditRowKey && eventEditColumnName) {
                emit('cellValueChanged', userData.value, event, eventEditRowKey, eventEditColumnName);
                emit('userDataChanged', userData.value);
                eventEditRowKey = null;
                eventEditColumnName = null;
            }
            const { saveChangesButtonState } = toolbarState;
            const saveButtonDisable = userData.value.treeListChanges.length <= 0;
            saveChangesButtonState.instance?.option('disabled', saveButtonDisable);
            saveChangesButtonState.disabled = saveButtonDisable;
            refs.value['isSaveButtonDisabled'] = saveButtonDisable;
        }
    }
};

const onEditingStart = event => {
    if (props.lockedRows.includes(event.key)) {
        event.cancel = true;
    }
};

const onEditorPreparing = event => {
    const { parentType, editorOptions, dataField, row, editorName } = event;

    if (parentType === 'dataRow' && editorName !== 'dxCheckBox' && editorOptions) {
        const originalValue = getOriginalValue(row.key, dataField);

        editorOptions.onValueChanged = args => {
            if (event.row.isNewRow) {
                updateInsert(row.key, dataField, args.value);
                return;
            }
            updateChanges(row.key, dataField, args.value, originalValue);
        };
    }

    if (parentType === 'filterRow' && dataField) {
        editorOptions.onValueChanged = args => {
            clearTimeout(filterDelayTimer.value);

            filterDelayTimer.value = setTimeout(() => {
                columnFilters.value = {
                    ...columnFilters.value,
                    [dataField]: args.value,
                };
                userSettingsData.value = {
                    ...userSettingsData.value,
                    filterRowValues: columnFilters.value,
                };
                vIsCurrentSettingsUser.value && emit('userSettingsChanged', userSettingsData.value);
            }, 2000);
        };

        editorOptions.onInitialized = ({ component }) => {
            if (!filterRowInstances.value[dataField]) filterRowInstances.value[dataField] = component;
        };

        if (Object.hasOwn(columnFilters.value, dataField)) {
            editorOptions.value = columnFilters.value[dataField];
        }
    }
};

const getOriginalValue = (key, dataField) => {
    const row = displayList.value.find(({ greyEminence }) => greyEminence === key);
    return row ? row[dataField] : null;
};

const updateInsert = (key, dataField, newValue) => {
    const changes = treeListInstance.value.option('editing.changes');
    const existingChange = changes.find(change => change.key === key && change.type === 'insert');
    let trimmedValue = newValue;
    if (typeof newValue === 'string') {
        trimmedValue = newValue.trim() === '' ? null : newValue.trim();
    }

    const updatedChangeData = { ...existingChange.data, [dataField]: trimmedValue };
    existingChange.data = updatedChangeData;
};

const updateChanges = (key, dataField, newValue, originalValue) => {
    const changes = treeListInstance.value.option('editing.changes');
    let trimmedValue = newValue;
    if (typeof newValue === 'string') {
        trimmedValue = newValue.trim() === '' ? null : newValue.trim();
    }

    changes.push({
        key: key,
        type: 'update',
        data: { [dataField]: trimmedValue },
    });

    const existingChange = changes.find(change => change.key === key && change.type === 'update');
    const condition =
        (String(trimmedValue) === String(originalValue) || (!newValue && !originalValue)) && existingChange;

    if (condition) {
        const properties = Object.keys(existingChange.data);
        const countOfProperties = properties.length;
        let updatedChanges;

        if (countOfProperties <= 1) {
            updatedChanges = changes.filter(change => change.key !== key);
        } else {
            updatedChanges = changes.map(change => {
                const condition = change.key === key && properties.includes(dataField);
                if (condition) {
                    const { [dataField]: _, ...rest } = change.data; // eslint-disable-line
                    change.data = { ...rest };
                }
                return change;
            });
        }

        treeListInstance.value.option('editing.changes', updatedChanges);
        return;
    }

    if (existingChange) {
        existingChange.data[dataField] = newValue;
    }

    treeListInstance.value.option('editing.changes', changes);
};

const onEditorPrepared = ({ parentType, editorElement, row, command, dataField }) => {
    if (parentType !== 'dataRow') return;

    const lockedCase = command === 'select' && row && props.lockedRows.includes(row.key);
    if (lockedCase) editorElement.classList.add('hidden');

    if (editorElement) {
        const originalValue = String(getOriginalValue(row.key, dataField));

        editorElement.addEventListener('input', ({ target }) => {
            const newValue = target.value;
            const cellClasses = editorElement.closest('td').classList;

            defferedUpdateUserData(row.key, dataField, newValue);

            const isCellValueModified = !(originalValue === newValue || (!originalValue && !newValue));

            if (isCellValueModified) cellClasses.add('dx-cell-modified');
            if (!isCellValueModified) cellClasses.remove('dx-cell-modified');
        });
    }
};

const defferedUpdateUserData = (key, dataField, value) => {
    const changes = [...userData.value.treeListChanges];
    const existingChange = changes.find(change => change.key === key);

    if (existingChange) {
        existingChange.data[dataField] = value;
    } else {
        changes.push({
            key: key,
            type: 'update',
            data: { [dataField]: value },
        });
    }

    if (timeoutId.value) clearTimeout(timeoutId.value);

    timeoutId.value = setTimeout(() => {
        userData.value = {
            ...userData.value,
            treeListChanges: changes,
        };
    }, 500);
};

const onCopyClick = async () => {
    treeListInstance.value.getScrollable().scrollTo(0);

    setTimeout(async () => {
        const key = treeListInstance.value.option('focusedRowKey');
        const sourceRowData = await treeListInstance.value.getDataByKeys([key]);
        const newRowData = {
            ...sourceRowData[0],
            [props.meta.keyExpr]: null,
            greyEminence: `${Math.random()}`,
        };

        const insert = {
            type: 'insert',
            key: newRowData.greyEminence,
            data: newRowData,
            index: 0,
        };

        const changesOptions = [...treeListInstance.value.option('editing.changes'), insert];

        userData.value = {
            ...userData.value,
            treeListChanges: changesOptions,
        };
        emit('userDataChanged', userData.value);

        await treeListInstance.value.option('editing.changes', changesOptions);
        await treeListInstance.value.refresh();

        setFocus(newRowData.greyEminence);
    }, 200);
};

const onAddRootRowClick = async () => {
    treeListInstance.value.getScrollable().scrollTo(0);

    setTimeout(async () => {
        let key = null;
        if (displayList.value.length > 0) {
            key = treeListInstance.value.option('focusedRowKey');
        }
        const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
        const parentIdExpr = props.meta.parentIdExpr;
        const parentId = key ? focusedRowData[0][parentIdExpr] : null;
        const blankRowData = metaColumns.value.reduce(
            (acc, key) => ({
                ...acc,
                [key]: null,
            }),
            {}
        );

        const insert = {
            ...blankRowData,
            isNewRow: true,
            greyEminence: `${Math.random()}`,
            [parentIdExpr]: parentId,
        };

        //TODO: сделать проверку на тип первичного ключa
        const newRow = {
            type: 'insert',
            key: insert.greyEminence,
            index: 0,
            data: insert,
        };

        const changesOptions = [...treeListInstance.value.option('editing.changes'), newRow];
        userData.value = {
            ...userData.value,
            treeListChanges: changesOptions,
        };
        emit('userDataChanged', userData.value);

        await treeListInstance.value.option('editing.changes', changesOptions);
        await treeListInstance.value.refresh();

        setFocus(newRow.greyEminence);
    }, 200);
};

const onAddChildRowClick = async () => {
    treeListInstance.value.getScrollable().scrollTo(0);

    setTimeout(async () => {
        const key = treeListInstance.value.option('focusedRowKey');
        if (key === null) return;

        const parentIdExpr = props.meta.parentIdExpr;
        const blankRowData = metaColumns.value.reduce(
            (acc, key) => ({
                ...acc,
                [key]: null,
            }),
            {}
        );
        const insert = {
            ...blankRowData,
            isNewRow: true,
            greyEminence: `${Math.random()}`,
            [parentIdExpr]: key,
        };

        //TODO: сделать проверку на тип первичного ключа
        const newRow = {
            key: insert.greyEminence,
            data: insert,
            index: 0,
            type: 'insert',
        };

        const changesOptions = [...treeListInstance.value.option('editing.changes'), newRow];
        userData.value = {
            ...userData.value,
            treeListChanges: changesOptions,
        };
        emit('userDataChanged', userData.value);

        await treeListInstance.value.option('editing.changes', changesOptions);
        await treeListInstance.value.refresh();

        setTimeout(() => {
            treeListInstance.value.expandRow(key);
        }, 300);

        setFocus(newRow.greyEminence);
    }, 200);
};

function expandParentRec(key) {
    const rows = props.dataSource.filter(row => row[props.meta.keyExpr] === key);
    if (rows.length > 0) {
        const row = rows[0];
        const keyParent = row[props.meta.parentIdExpr];
        let index = -1;
        if (keyParent) {
            index = treeListInstance.value.getRowIndexByKey(keyParent);
            if (index === -1) {
                expandParentRec(keyParent);
                treeListInstance.value.expandRow(key);
            } else {
                let rowElement = treeListInstance.value.getRowElement(index);
                treeListInstance.value.getScrollable().scrollToElement(rowElement);
                treeListInstance.value.expandRow(keyParent);
                index = treeListInstance.value.getRowIndexByKey(key);
                rowElement = treeListInstance.value.getRowElement(index);
                treeListInstance.value.getScrollable().scrollToElement(rowElement);
            }
        } else {
            index = treeListInstance.value.getRowIndexByKey(key);
            const rowElement = treeListInstance.value.getRowElement(index);
            treeListInstance.value.getScrollable().scrollToElement(rowElement);
            treeListInstance.value.expandRow(key);
        }
    }
}

const setFocus = key => {
    let index = treeListInstance.value.getRowIndexByKey(key);
    if (index === -1) {
        expandParentRec(key);
        index = treeListInstance.value.getRowIndexByKey(key);
    }
    const rowElement = treeListInstance.value.getRowElement(index);
    treeListInstance.value.getScrollable().scrollToElement(rowElement);
    setTimeout(() => {
        treeListInstance.value.option('focusedRowKey', key);
        // treeListInstance.value.option('focusedRowIndex', index);
    }, 500);
};

const setSelected = keys => {
    for (const key of keys) {
        let index = treeListInstance.value.getRowIndexByKey(key);
        if (index === -1) {
            expandParentRec(key);
            index = treeListInstance.value.getRowIndexByKey(key);
            console.log(index);
        }
    }
    setTimeout(() => {
        treeListInstance.value.option('selectedRowKeys', keys);
    }, 500);
};

const onDeleteClick = async () => {
    const dataToDelete =
        props.meta.selectionMode === 'single' ? [focusedRow.value.data] : treeListInstance.value.getSelectedRowsData();

    processDeleting(dataToDelete);
};

const processDeleting = rowsToDelete => {
    if (rowsToDelete.length === 1 && rowsToDelete[0].isNewRow) {
        const index = treeListInstance.value.getRowIndexByKey(focusedRow.value.node.key);
        treeListInstance.value.deleteRow(index);
        emit('changesSavedSuccessfully');
        return;
    }

    const allDescendants = accumulateAllDescendants(rowsToDelete);

    const processedData = {
        insertDataArgs: [],
        updateDataArgs: [],
        deleteDataArgs: allDescendants,
    };

    emit('deleteClick', processedData);
};

const accumulateAllDescendants = rowsToDelete => {
    const parentId = props.meta.parentIdExpr;
    const parentToChildrenMap = new Map();

    displayList.value.forEach(row => {
        if (parentToChildrenMap.has(row[parentId])) {
            const existed = parentToChildrenMap.get(row[parentId]);
            parentToChildrenMap.set(row[parentId], [...existed, row]);
        } else {
            parentToChildrenMap.set(row[parentId], [row]);
        }
    });

    const allDescendantsSet = new Set();

    const addChildrenToSet = row => {
        if (row) allDescendantsSet.add(row);

        if (!parentToChildrenMap.has(row?.greyEminence)) {
            return null;
        }

        const children = parentToChildrenMap.get(row.greyEminence);

        children.forEach(child => addChildrenToSet(child));
    };

    rowsToDelete.forEach(row => addChildrenToSet(row));

    return [...allDescendantsSet];
};

const onContextMenuPreparing = event => {
    if (event.target !== 'content') return;
    if (!event.row) return;
    if (props.lockedRows.includes(event.row.key)) return;

    if (!event.items) event.items = [];
    if (props.editing?.allowAdding) {
        event.items.push({
            text: 'Копировать',
            onItemClick: onCopyClick,
        });
    }
    if ((props.editing?.enableDeleting && event.row) || event.row?.data?.isCopy || event.row?.isNewRow) {
        const rowData = {
            ...event.row.data,
            rowIndexFront: event.rowIndex,
        };

        event.items.push({
            text: 'Удалить',
            onItemClick: () => processDeleting([rowData]),
        });
    }

    if (props.meta?.enableCutAndPut && event.row) {
        event.items.push({
            text: 'Вырезать',
            onItemClick: () => onCutClick(event),
        });
        const putYourself = !!cutRow.value && cutRow.value.key === event.row.key;
        const putDescedant = !!cutRow.value && isDescendant(event.row.key, cutRow.value?.key);
        event.items.push({
            text: 'Вставить',
            onItemClick: () => {
                try {
                    onPutClick(event);
                } finally {
                    const menuElement = document.querySelector('.dx-context-menu');
                    if (menuElement) {
                        const instance = ContextMenu.getInstance(menuElement);
                        if (instance) {
                            instance.hide();
                        }
                    }
                }
            },
            disabled: !cutRow.value || putYourself || putDescedant,
            hint: !cutRow.value
                ? 'Сначала надо вырезать строку'
                : putYourself
                ? 'Нельзя вставить строку в саму себя'
                : !putDescedant
                ? 'Нельзя вставить родителя в потомка'
                : undefined,
        });
    }
    if (treeListInstance.value.option('disabled')) {
        event.cancel = true;
    }
    emit('coreContextMenuPrepared', event);
};

const onSaveClick = async event => {
    event.cancel = true;

    const processedData = await prepareChangesForSaving();
    emit('saveClick', processedData, reorderedRows.value, event);
};

const prepareChangesForSaving = async () => {
    const processedData = {
        insertDataArgs: [],
        updateDataArgs: [],
        deleteDataArgs: [],
    };

    for (const { type, data, key } of userData.value.treeListChanges) {
        let processedRowData;

        if (type === 'insert') {
            processedRowData = { ...data };
        } else {
            const fullRowFromDs = displayList.value.find(row => row.greyEminence === key);
            processedRowData = { ...fullRowFromDs, ...data };
        }

        Object.keys(processedRowData).forEach(key => {
            if (booleanColumns.value.includes(key)) processedRowData[key] = processedRowData[key] ? 1 : 0;
            if (processedRowData[key] === '') processedRowData[key] = null;
        });

        switch (type) {
            case 'update': {
                if (processedRowData.isNewRow) {
                    processedData.insertDataArgs.push(processedRowData);
                    break;
                }

                processedData.updateDataArgs.push({ ...processedRowData });
                break;
            }
            case 'insert': {
                const blankRow = metaColumns.value.reduce(
                    (obj, key) => ({
                        ...obj,
                        [key]: null,
                    }),
                    {}
                );
                const merge = { ...blankRow, ...processedRowData };
                processedData.insertDataArgs.push(merge);
                break;
            }
            default:
                break;
        }
    }

    return processedData;
};

const onRefreshClick = async () => {
    const processedData = await prepareChangesForSaving();
    if (rowToCut.value) {
        rowToCut.value.item_id = null;
    } else {
        rowToCut.value = { item_id: null };
    }
    emit('refreshClick', userData.value.treeListChanges, processedData);
};

const onExpandOrCollapseClick = () => {
    const { expandOrCollapseButtonState } = toolbarState;

    const action = expandOrCollapseButtonState.instance.option['icon'];

    if (action === 'expand') {
        expandAll();
    }
    if (action === 'collapse') {
        collapseAll();
    }
};
const expandAll = () => {
    const { expandOrCollapseButtonState } = toolbarState;

    // if (expandedRowKeys.value.length <= displayList.value.length) {
    expandedRowKeys.value = displayList.value.map(item => item.greyEminence);
    //}

    expandOrCollapseButtonState.icon = 'collapse';
    expandOrCollapseButtonState.hint = 'Свернуть все';

    if (expandOrCollapseButtonState.instance) {
        expandOrCollapseButtonState.instance.option['icon'] = 'collapse';
        expandOrCollapseButtonState.instance.option['hint'] = 'Свернуть все';
    }

    refs.value['expandOrCollapseButtonStateHint'] = 'Свернуть все';
};

const collapseAll = () => {
    if (!props.toRemoveToolbarItemsNames.includes('expandOrCollapseButton')) {
        const { expandOrCollapseButtonState } = toolbarState;

        if (expandedRowKeys.value.length) {
            expandedRowKeys.value = [];
        }

        expandOrCollapseButtonState.icon = 'expand';
        expandOrCollapseButtonState.hint = 'Развернуть все';
        if (expandOrCollapseButtonState.instance) {
            expandOrCollapseButtonState.instance.option['icon'] = 'expand';
            expandOrCollapseButtonState.instance.option['hint'] = 'Развернуть все';
        }
        refs.value['expandOrCollapseButtonStateHint'] = 'Развернуть все';
    }
    if (props.changeRouteComponent && expandedRowKeys.value.length) {
        expandedRowKeys.value = [];
    }
};

const preferencesCheckChanged = ({ value }) => {
    const newItems = [];
    const { preferencesButtonState } = toolbarState;
    const disableValue = !!value;

    preferencesButtonState.instance?.option('items').forEach(item => {
        if (item.id === 2) item.disabled = disableValue;
        newItems.push(item);
    });

    userSettingsData.value = {
        ...userSettingsData.value,
        preferencesState: disableValue,
    };
    preferencesButtonState.items = newItems;
    preferencesButtonState.instance?.option('items', newItems);
    emit('userSettingsChanged', userSettingsData.value);
};

const onDragStart = event => {
    treeListInstance.value.option('focusedRowKey', event.itemData.greyEminence);
};

const onDragChange = event => {
    const { component, itemData, toIndex, dropInsideItem } = event;
    const { parentIdExpr } = props.meta;
    const visibleRows = component.getVisibleRows();
    const sourceNode = component.getNodeByKey(itemData.greyEminence);
    const targetRow = visibleRows[toIndex];

    if (!targetRow) {
        event.cancel = true;
        return;
    }

    let targetNode = visibleRows[toIndex].node;
    const sourceId = sourceNode.data.greyEminence;

    // Запрет на вставку родителя в потомка (используем isDescendant)
    const isParentToChild = isDescendant(targetNode.data.greyEminence, sourceId);

    // Общие проверки для всех случаев
    const isInvalidOperation =
        // Запрет для новых строк
        (targetNode.data.isNewRow && !sourceNode.data.isNewRow) ||
        // Запрет перетаскивания на себя
        sourceId === targetNode.data.greyEminence ||
        // Запрет перемещения в том же уровне
        (!dropInsideItem && sourceNode.data[parentIdExpr] === targetNode.data[parentIdExpr]) ||
        isParentToChild;
    if (isInvalidOperation) {
        event.cancel = true;
        // console.log('onDragChange',event.cancel);
        return;
    }
    //эмитим для особенных случаев
    emit('dragChange', {
        event,
        sourceData: sourceNode.data,
        targetData: targetNode.data,
    });
};

const updateReorderedRows = (key, updatedParentId) => {
    reorderedRows.value = reorderedRows.value.map(row => {
        if (row.key === key) {
            row.updatedParentId = updatedParentId;
        }
        return row;
    });
};

const addNewReorderedRow = (key, updatedParentId) => {
    reorderedRows.value.push({
        key,
        updatedParentId,
    });
};

const onDragReorder = async event => {
    console.log('onDragReorder cancel', event.cancel);
    const { component, itemData, toIndex, fromIndex, dropInsideItem } = event;
    const { parentIdExpr } = props.meta;

    const visibleRows = component.getVisibleRows();
    const { greyEminence } = itemData;
    const match = reorderedRows.value.find(row => row.key === greyEminence);

    if (dropInsideItem) {
        const newParentId = visibleRows[toIndex].key;

        if (match) {
            updateReorderedRows(greyEminence, newParentId);
        } else {
            addNewReorderedRow(greyEminence, newParentId);
        }
    } else {
        const calculatedToIndex = fromIndex > toIndex ? toIndex - 1 : toIndex;
        const targetData = calculatedToIndex >= 0 ? visibleRows[calculatedToIndex].node.data : null;
        const isRowExpanded = targetData && component.isRowExpanded(targetData.greyEminence);
        const newParentId = targetData ? (isRowExpanded ? targetData.greyEminence : targetData[parentIdExpr]) : null;

        if (match) {
            updateReorderedRows(greyEminence, newParentId);
        } else {
            addNewReorderedRow(greyEminence, newParentId);
        }
    }

    const resultParentId = reorderedRows.value.find(row => row.key === itemData.greyEminence)?.updatedParentId;
    const updatedRowData = event.itemData;

    const changes = treeListInstance.value.option('editing.changes');

    const change = {
        type: 'update',
        data: {
            ...updatedRowData,
            [parentIdExpr]: resultParentId,
        },
        key: updatedRowData['greyEminence'],
    };

    const existingChange = changes.find(item => item.key === change.key && item.type === 'update');

    if (existingChange) {
        existingChange.data = {
            ...existingChange.data,
            [parentIdExpr]: change.data[parentIdExpr],
        };

        const updatedChanges = [
            ...changes.filter(item => !(item.key === change.key && item.type === 'update')),
            existingChange,
        ];

        await treeListInstance.value.option('editing.changes', updatedChanges);
    } else {
        const changesOptions = [...treeListInstance.value.option('editing.changes'), change];
        await treeListInstance.value.option('editing.changes', changesOptions);
    }

    userData.value = {
        ...userData.value,
        reorderedRows: reorderedRows.value,
    };
    emit('userDataChanged', userData.value);

    const { saveChangesButtonState } = toolbarState;
    const saveButtonDisable = userData.value.treeListChanges.length <= 0;
    saveChangesButtonState.instance?.option('disabled', saveButtonDisable);
    saveChangesButtonState.disabled = saveButtonDisable;

    await treeListInstance.value.refresh();
    emit('dragReorder', event);
};

const setReorderedRows = arr => {
    reorderedRows.value = arr;
    userData.value = {
        ...userData.value,
        reorderedRows: arr,
    };
    emit('userDataChanged', userData.value);
};

const setSearchPanelValue = value => {
    searchQuery.value = value;
    userData.value = {
        ...userData.value,
        searchPanelValue: value,
    };
    emit('userDataChanged', userData.value);
};

const delay = ms => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

const getTreeListDataSource = async (ms = 500) => {
    await delay(ms);
    return displayList.value;
};

const highLightMatches = (rowId, dataField, className) => {
    const matchingFields = filterMatches.value.get(rowId);

    if (matchingFields && matchingFields.has(dataField)) {
        return className;
    } else {
        return '';
    }
};

const refreshSaveButtonDisable = () => {
    onOptionChanged({
        fullName: 'editing.changes',
        value: treeListInstance.value.option('editing.changes'),
    });
};

const setColumnFiltersValues = colFilters => {
    columnFilters.value = colFilters;
};

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});

defineExpose({
    treeListInstance,
    getTreeListDataSource,
    expandAll,
    collapseAll,
    setSelected,
    setReorderedRows,
    setSearchPanelValue,
    highLightMatches,
    refreshSaveButtonDisable,
    setColumnFiltersValues,
    prepareChangesForSaving,
    updateChanges,
    getButtonsRefs: async () => {
        await delay(100);
        return refs;
    },
    userData,
});
</script>
<style lang="scss" scoped>
:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}

.tree-list-border {
    border: 1px solid #e5e7eb;
}

.highlight-cell {
    background-color: #ffff9b;
}

.bg-color {
    background-color: red !important;
}

:deep(.dx-clear-button-area) {
    margin-left: 8px !important; /* Внешний отступ от поля ввода */
}
</style>
